import type { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import type { Editor } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import React from 'react';

import type { EditorFeatures } from '../types';
import { useTiptapExtensions } from './useTiptapExtensions';

interface UseTiptapEditorCoreProps {
  features: EditorFeatures;
  value: string;
  onChange: (content: string) => void;
  editorRef: React.MutableRefObject<Editor | null>;
  isInitFocus?: boolean;
  enableMentions?: boolean;
  placeholderText?: string;
  channelName?: string;
  channelMembers?: ChannelMember[];
  currentUserId?: number;
  currentUserType?: string;
  isLoggedIn?: boolean;
  isCommunityEditor?: boolean;
  enablePasteFiltering?: boolean;
  removeTenantMentions?: boolean;
}

export function useTiptapEditorCore({
  features,
  value,
  onChange,
  editorRef,
  isInitFocus = false,
  enableMentions = false,
  placeholderText = '',
  channelMembers,
  currentUserId,
  currentUserType,
  isLoggedIn = true,
  isCommunityEditor = false,
  enablePasteFiltering = false,
  removeTenantMentions = false,
}: UseTiptapEditorCoreProps) {
  const extensions = useTiptapExtensions({
    enabled: features,
    enableMentions,
    placeholderText,
    channelMembers,
    currentUserId,
    currentUserType,
    isLoggedIn,
    enablePasteFiltering,
    removeTenantMentions,
  });

  const editor = useEditor({
    extensions,
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    autofocus: isInitFocus,
    editable: true,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    editorProps: {
      ...(isCommunityEditor && {
        transformPastedHTML: (html: string) => {
          return html.replace(/color\s*:\s*[^;"']*;?\s*/gi, '');
        },
      }),
      attributes: {
        class: 'h-full',
      },
    },
  });

  React.useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // 内容を更新する際にいったんカーソル位置を保持して
      const { from, to } = editor.state.selection;
      editor.commands.setContent(value);
      // 内容を更新した後にカーソル位置を復元する
      editor.commands.setTextSelection({
        from: Math.min(from, editor.state.doc.content.size),
        to: Math.min(to, editor.state.doc.content.size),
      });
    }
  }, [editor, value]);

  return {
    editor,
  };
}
